import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import numberHumanizer from '@/helpers/numberHumanizer';
import { useRef } from 'react';
import { Card, Alert } from 'react-bootstrap';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import type { EChartsOption } from 'echarts';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import timeStampToDate from '@/helpers/timeStampToDate';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import type { SerpSearchVolumes } from '@/app/Serp/types/serpApiTypes';

echarts.use([BarChart, TooltipComponent, GridComponent, LegendComponent]);

interface WordSearchVolumeProps {
  data?: SerpSearchVolumes | null;
}

const WordSearchVolume = ({ data }: WordSearchVolumeProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const search_volume_miztols = data?.search_volume_miztols;
  const dates = data?.dates ?? [];
  const values = data?.values ?? [];
  return (
    <Card ref={cardRef} className="h-md-100">
      <FalconCardHeader
        title={
          <div>
            میزان جستجوی کلمه
            <HelpTooltip html={serpTooltips.wordSearchVolume} />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
      />
      {!search_volume_miztols && dates.length === 0 ? (
        <Card.Body>
          <Alert
            variant={'warning'}
            className={'d-flex align-items-center fs--1 p-2'}
          >
            <div>
              اگر اطلاعاتی برای میزان جستجوی این کلمه وجود ندارد ممکن است به
              دلایل زیر باشد: <br /> <br /> یک: کلمه طولانی و فاقد جستجوی زیاد{' '}
              <br />
              دو: کلمه غلط املایی داره <br /> سه: کلمه جستجویی نداره <br />
              چهار: کلمه غیرمجاز هست <br /> پنج: کلمه دارای نیم فاصله هست <br />
              شش: کلمه خارج از قوانین موتورجستجوی گوگل هست <br />
              <a
                href="https://help.mizfa.tools/faq/reason-for-not-displaying-keyword-search-volume/"
                rel="noreferrer"
                target="_blank"
              >
                اطلاعات بیشتر
              </a>
            </div>
          </Alert>
        </Card.Body>
      ) : (
        <Card.Body className="fs--1 p-3">
          <h5 className="text-1100 fw-bold mb-2 mt-n1">
            {numberHumanizer(search_volume_miztols)}
            <span className="fs--1 fw-normal text-700">
              {' '}
              (میانگین جستجوی ماهانه){' '}
            </span>
          </h5>
          <div>
            <SearchVolumeChart
              dates={dates}
              count={100}
              series={[
                {
                  name: 'روند جستجو',
                  data: values.map((value, i) => ({
                    name: dates[i],
                    value: value,
                    itemStyle: {
                      color: getColor(
                        'navbar-vertical-default-link-active-color'
                      )
                    }
                  }))
                }
              ]}
            />
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

export default WordSearchVolume;

type SearchVolumeSeriesDataItem = {
  name: string | number;
  value: number;
  itemStyle: { color: string };
};

type SearchVolumeSeriesItem = {
  name: string;
  data: SearchVolumeSeriesDataItem[];
  itemStyle?: { color: string };
};

interface SearchVolumeChartProps {
  series: SearchVolumeSeriesItem[];
  dates: (string | number)[];
  count: number;
}

const SearchVolumeChart = ({
  series,
  dates,
  count
}: SearchVolumeChartProps) => {
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);

  const chart_series = series.slice(count * -1).map(item => {
    return {
      animation: false,
      type: 'bar',
      name: item.name,
      data: item.data,
      stack: 'one',
      itemStyle: item.itemStyle,
      showSymbol: false,
      symbol: 'circle',
      smooth: false,
      emphasis: {
        itemStyle: {
          color: getColor('primary')
        },
        lineStyle: {
          color: getColor('primary')
        }
      }
    };
  });

  /*کانفیگ های مربوط به نمودار */
  const options = {
    textStyle: {
      fontFamily: 'dana'
    },

    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',
      trigger: 'axis',
      padding: [7, 10],
      // position: function (pos, params, el, elRect, size) {
      //   var obj = {
      //     top: -30,
      //     left: -130
      //   };
      //   obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 120;
      //   return obj;
      // },
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      formatter: (params: any) => {
        return `
          <div>
          <div style="font-family: dana !important;">
          <div class="mb-2 d-flex gap-1 fs--2">
        تاریخ جستجو <span style="direction:rtl !important;">${timeStampToDate(Number(params[0].name)).slice(8).slice(0, -3).toPersianDigit()}</span>
          </div>  
          <div style="direction:rtl;" class="d-flex flex-column">
          <span class="d-flex justify-content-between" style="direction:ltr;margin:0;padding:0"><span class="fw-bolder">${params[0].marker} ${numberHumanizer(params[0].value).toString().toPersianDigit()}</span>
            </div>
          </div>`;
      },
      transitionDuration: 0,
      axisPointer: {
        type: 'shadow'
      }
    },
    xAxis: {
      type: 'category',
      data: dates
        .slice(count * -1)
        .map(item =>
          timeStampToDate(Number(item)).slice(8).slice(0, -3).toPersianDigit()
        ),
      axisLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisTick: { show: false },
      axisLabel: {
        show: true,

        color: getColor('gray-800'),
        margin: 5
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        show: false,
        color: getColor('gray-800'),
        margin: 5
      },
      splitLine: {
        show: false,
        lineStyle: {
          color: getColor('gray-200')
        }
      },
      axisTick: { show: false },
      axisLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      min: 0
    },
    series: chart_series,
    grid: { right: '8%', left: '8%', bottom: '10%', top: '20%' }
  } as EChartsOption;

  return (
    <ReactEChartsCore
      echarts={echarts}
      ref={chartRef}
      option={options}
      style={{ height: '10rem' }}
    />
  );
};
